import { useState } from 'react';
import { FiChevronDown, FiExternalLink, FiX } from 'react-icons/fi';

import { Icon } from '@iconify/react';

import {
  Backdrop,
  Block,
  BlockBody,
  BlockHeader,
  CloseButton,
  DrawerHeader,
  Footer,
  Intro,
  Item,
  Panel,
} from './styles';

interface IProps {
  open: boolean;
  onClose: () => void;
}

const WHATSAPP_URL =
  'https://api.whatsapp.com/send/?phone=5511945491306&text=Ol%C3%A1!%20Zignet';

interface BlocoItem {
  icon?: string;
  titulo: string;
  desc: string;
}

interface Bloco {
  id: string;
  icon: string;
  title: string;
  texto?: string;
  itens?: BlocoItem[];
  destaque?: boolean;
}

// Conteudo definido pelo comercial na ZIG-388 (comment 13091) + tela de referencia.
const BLOCOS: Bloco[] = [
  {
    id: 'como-funciona',
    icon: 'mdi:calculator-variant-outline',
    title: '1. Como funciona o simulador',
    texto:
      'Informe o valor da venda e escolha a forma de pagamento para visualizar uma estimativa do valor líquido que você poderá receber após a aplicação das taxas.',
  },
  {
    id: 'influencia',
    icon: 'mdi:tune-variant',
    title: '2. O que pode influenciar nas taxas',
    itens: [
      {
        icon: 'mdi:credit-card-outline',
        titulo: 'Tipo de pagamento',
        desc: 'Débito ou crédito podem ter condições diferentes.',
      },
      {
        icon: 'mdi:clock-outline',
        titulo: 'Prazo de recebimento',
        desc: 'Receber antes pode alterar o valor líquido disponível.',
      },
      {
        icon: 'mdi:file-document-outline',
        titulo: 'Plano contratado',
        desc: 'As condições podem variar conforme o plano comercial ativo.',
      },
      {
        icon: 'mdi:trending-up',
        titulo: 'Volume de vendas',
        desc: 'Alguns planos podem considerar o volume mensal de transações.',
      },
      {
        icon: 'mdi:card-multiple-outline',
        titulo: 'Parcelamento',
        desc: 'Vendas parceladas podem ter taxas diferentes de vendas à vista.',
      },
    ],
  },
  {
    id: 'prazos',
    icon: 'mdi:help-circle-outline',
    title: '3. Entenda os prazos de recebimento',
    itens: [
      {
        titulo: 'D+1',
        desc: 'Você recebe no próximo dia útil após a confirmação da venda.',
      },
      {
        titulo: 'No vencimento',
        desc: 'Você recebe na data de vencimento da fatura do cliente, sem antecipação do valor.',
      },
    ],
  },
  {
    id: 'aviso',
    icon: 'mdi:shield-check-outline',
    title: '4. Aviso importante',
    texto:
      'Os valores exibidos no simulador são apenas uma referência. A taxa final será aplicada conforme as condições do plano contratado e poderá ser confirmada nos canais oficiais da Zignet.',
    destaque: true,
  },
];

export const InfoDrawer = ({ open, onClose }: IProps) => {
  const [aberto, setAberto] = useState<string | null>(null);

  const toggle = (id: string) => setAberto((prev) => (prev === id ? null : id));

  return (
    <>
      <Backdrop $open={open} onClick={onClose} />
      <Panel $open={open} role="dialog" aria-label="Entenda as taxas do simulador">
        <DrawerHeader>
          <h3>Entenda as taxas do simulador</h3>
          <CloseButton type="button" onClick={onClose} aria-label="Fechar">
            <FiX />
          </CloseButton>
        </DrawerHeader>

        <Intro>
          As taxas apresentadas são estimativas e ajudam você a entender quanto
          poderá receber em cada venda. O valor final pode variar conforme o
          plano contratado, forma de pagamento, prazo de recebimento e condições
          comerciais do seu negócio.
        </Intro>

        {BLOCOS.map((bloco) => (
          <Block key={bloco.id} $destaque={bloco.destaque}>
            <BlockHeader
              type="button"
              onClick={() => toggle(bloco.id)}
              $open={aberto === bloco.id}
            >
              <span className="titulo">
                <Icon icon={bloco.icon} />
                {bloco.title}
              </span>
              <FiChevronDown className="chevron" />
            </BlockHeader>

            {aberto === bloco.id && (
              <BlockBody>
                {bloco.texto && <p>{bloco.texto}</p>}
                {bloco.itens?.map((item) => (
                  <Item key={item.titulo}>
                    {item.icon && <Icon icon={item.icon} />}
                    <div>
                      <strong>{item.titulo}</strong>
                      <span>{item.desc}</span>
                    </div>
                  </Item>
                ))}
              </BlockBody>
            )}
          </Block>
        ))}

        <Footer>
          Ainda tem dúvidas?{' '}
          <a href={WHATSAPP_URL} target="_blank" rel="noreferrer">
            Fale com nosso time de atendimento
            <FiExternalLink />
          </a>
        </Footer>
      </Panel>
    </>
  );
};
